import { ProductTypeEnum } from "@jasper/client-models";

export default {
  common: {
    yes: "Yes",
    no: "No",
    validate: "Validate",
    validated: "Validated",
    toProvide: "To provide",
    invalidValue: "Invalid value",
    dateFormat: "MM/DD/YYYY",
  },
  products: {
    [ProductTypeEnum.bridge]: "Bridge",
    [ProductTypeEnum.crown]: "Crown",
    [ProductTypeEnum.splintedCrown]: "Splinted Crown",
  },
  components: {
    teethSelect: {
      title: "Select teeth",
      legends: {
        toothNumbering: "FDI Numbering · ISO 3950",
        solidarizedElements: "Solidarized elements",
        splittedElements: "Splitted elements",
        solidarized_one: "Solidarized",
        solidarized_other: "Solidarized",
        solidarizationHelp:
          "Click on the link between two neighboring teeth to solidarize them",
        noSolidarization_zero: "All teeth are separated · no junction possible",
        noSolidarization_one: "All teeth are separated · one junction possible",
        noSolidarization_other:
          "All teeth are separated · {{count}} junctions possible",
      },
      labels: {
        right: "Right",
        left: "Left",
        mandibular: "Mandibular",
        maxillary: "Maxillary",
        tooth: "Tooth",
        groupButton: "Group teeth {{firstTooth}} and {{secondTooth}}",
      },
      tooltips: {
        groupButton: "Group the selected teeth",
      },
      buttons: {
        groupAll: "Group all adjacent teeth",
        dissociateAll: "Dissociate all teeth",
      },
    },
    droppableFileInput: {
      errors: {
        invalidType: "{{filename}} is invalid",
        alreadyExists: "{{filename}} has already been uploaded",
      },
      labels: {
        input: "Upload",
        selectFiles: "Select files",
      },
    },
    phoneNumberInput: {
      placeholder: "Enter a phone number",
      countryAriaLabel: "Select country",
      phoneNumberAriaLabel: "Enter phone number",
    },
    textAreaWithDroppableFile: {
      errors: {
        invalidType: "{{filename}} is invalid",
        alreadyExists: "{{filename}} has already been uploaded",
        fileNotUploaded: "The file has not been uploaded",
      },
      labels: {
        uploadFileButton: "Attach files",
      },
      placeholders: {
        textArea: "Enter text",
      },
    },
    userSelect: {
      placeholder: "Select a user",
    },
    saveableCard: {
      save: "Save",
      saved: "Saved",
    },
  },
  forms: {
    address: {
      labels: {
        addressName: "Address name",
        streetNumber: "Street number",
        streetName: "Street name",
        details: "Additional information",
        city: "City",
        postalCode: "Postal code",
        country: "Country",
      },
      tooltips: {
        streetNumber:
          "Street number with street name must be shorter than 33 characters",
        streetName:
          "Street number with street name must be shorter than 33 characters",
      },
    },
    materialCharacteristics: {
      errors: {
        material: "Choose a material",
      },
      placeholders: {
        material: "Choose a material",
      },
      labels: {
        material: "Material",
      },
    },
    inlayCoreCharacteristics: {
      errors: {
        hasClavette: "Tell us whether the post and core has multiple posts",
        scanPost: "Choose a scan post",
        material: "Choose a material",
      },
      placeholders: {
        scanPost: "Choose the scan post used",
        material: "Choose a material",
      },
      descriptions: {
        clavetedMaterial:
          "Only materials compatible with a post and core with multiple posts are offered.",
      },
      labels: {
        hasClavette: "Post and core with multiple posts?",
        scanPostType: "Scan post type",
        scanPost: "Scan post used",
        material: "Post and core material",
      },
    },
    shadeCharacteristics: {
      errors: {
        shadeType: "Choose a shade guide",
        shade: "Choose a shade",
      },
      placeholders: {
        shade: "Choose a shade",
      },
      labels: {
        shadeType: "Shade guide",
        shade: "Shade",
        isMultiShade: "Shade gradient",
      },
    },
    multiShadeCharacteristics: {
      errors: {
        incisalShade: "Choose an incisal shade",
        baseShade: "Choose a base shade",
        gingivalShade: "Choose a gingival shade",
      },
      placeholders: {
        incisalShade: "Choose an incisal shade",
        baseShade: "Choose a base shade",
        gingivalShade: "Choose a gingival shade",
      },
      labels: {
        incisalShade: "Incisal shade",
        baseShade: "Base shade",
        gingivalShade: "Gingival shade",
      },
    },
    product: {
      errors: {},
      placeholders: {},
      labels: {
        isPerTooth: "Different characteristics for each prosthesis",
        isPerToothConfirmation: {
          title: "Are you sure you want to continue?",
          message:
            "If you continue, the characteristics of each prosthesis will be reset.",
          confirm: "Yes",
          cancel: "Cancel",
        },
      },
    },
    orderInfo: {
      labels: {
        dentist: "Dentist",
      },
      placeholders: {
        dentist: "Search for a doctor",
      },
      searching: "Searching...",
      noDentistFound: "No doctor found",
    },
    patient: {
      labels: {
        firstName: "First name",
        lastName: "Last name",
        birthDate: "Date of birth",
        sex: "Sex",
      },
      sex: {
        MALE: "Male",
        FEMALE: "Female",
      },
    },
    implantCharacteristics: {
      subtitles: {
        scanBody: "Scan body",
      },
      descriptions: {
        scanBody:
          "The scan body allows us to find the 3D design libraries of your implant manufacturer. If you do not know the exact reference, provide the information you have or 'NA'.",
      },
      labels: {
        implantBrand: "Implant system brand",
        implantSystem: "Implant system",
        implantPlatform: "Platform / diameter",
        scanBody: "Reference used",
        missingReference: "I cannot find the reference for my implant system",
      },
      placeholders: {
        implantBrand: "Choose an implant system brand",
        implantSystem: "Choose an implant system",
        implantPlatform: "Choose a platform",
        scanBody: "Choose the reference",
      },
      errors: {
        implantBrand: "Please choose an implant system brand",
        implantSystem: "Please choose an implant system",
        implantPlatform: "Please choose a platform",
        scanBody: "Please choose the reference",
      },
    },
    abutmentCharacteristics: {
      choices: {
        standard: "Standard",
        standardOriginal: "Original abutment - manufacturer",
        standardGeneric: "Generic standard abutment",
        custom: "Custom",
      },
      errors: {
        abutmentType: "Please choose an abutment type",
        abutmentPreferences: "Please provide your preferences",
      },
      placeholders: {
        abutmentPreferences: "Provide your preferences",
      },
      labels: {
        abutmentType: "Abutment type",
        abutmentPreferences: "Abutment preferences",
        standardAbutmentType: "Standard abutment type",
      },
      descriptions: {
        abutmentPreferences:
          "If you have specific preferences for a standard abutment (Abutment height, angled, etc.)",
      },
    },
    implantedItemCharacteristics: {
      choices: {
        cementRetained: "Cement retained",
        screwRetained: "Screw retained",
      },
      errors: {
        retentionType: "Please choose a retention type",
      },
      labels: {
        retentionType: "Retention type",
      },
    },
  },
};
